import { useEffect, useMemo, useRef, useState } from 'react';
import { CheckIcon, ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import {
    type FilterOption,
    type FilterOptionItem,
    normalizeFilterOption,
    filterOptionMatches
} from '@odo/shared-ui';

// Single-select searchable combobox for forms. Same option list shape as
// FilterDropdown (label + sublabel + keyword matching) but styled as a form
// input and closing on selection.

interface SearchSelectProps {
    value: string;
    options: readonly FilterOption[];
    onChange: (value: string) => void;
    placeholder: string;
    searchPlaceholder?: string;
    disabled?: boolean;
    // Fired on every keystroke in the search box. Lets a caller drive a
    // server-side search (e.g. refetching options) in addition to the
    // built-in client-side filtering of the options already provided.
    onSearchChange?: (search: string) => void;
}

export function SearchSelect({
    value,
    options,
    onChange,
    placeholder,
    searchPlaceholder = 'Search...',
    disabled = false,
    onSearchChange
}: SearchSelectProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    const normalized = useMemo<FilterOptionItem[]>(
        () => options.map(normalizeFilterOption),
        [options]
    );
    const filtered = useMemo(
        () => normalized.filter((o) => filterOptionMatches(o, search)),
        [normalized, search]
    );
    const selected = normalized.find((o) => o.value === value);

    const resetSearch = () => {
        setSearch('');
        onSearchChange?.('');
    };

    useEffect(() => {
        if (!isOpen) return;
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                resetSearch();
            }
        };
        // Capture phase on window runs before the modal's own Escape listener, so stopping the
        // event here closes only the dropdown, not the modal around it.
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return;
            event.stopPropagation();
            setIsOpen(false);
            resetSearch();
        };
        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('keydown', handleKeyDown, true);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('keydown', handleKeyDown, true);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    useEffect(() => {
        if (isOpen) searchInputRef.current?.focus();
    }, [isOpen]);

    const handleSelect = (optionValue: string) => {
        onChange(optionValue);
        setIsOpen(false);
        resetSearch();
    };

    return (
        <div className="relative" ref={containerRef}>
            <button
                type="button"
                disabled={disabled}
                onClick={() => setIsOpen((v) => !v)}
                className={`flex items-center justify-between gap-2 w-full px-3 py-2 border rounded-md shadow-sm text-sm text-left transition-colors focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed ${
                    isOpen ? 'border-gray-700' : 'border-gray-300'
                }`}
            >
                <span className={`truncate ${selected ? 'text-gray-700' : 'text-gray-400'}`}>
                    {selected?.label ?? placeholder}
                </span>
                <ChevronDownIcon
                    className={`h-4 w-4 text-gray-400 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                />
            </button>

            {isOpen && (
                <div className="absolute left-0 right-0 mt-1 z-30 bg-white shadow-lg rounded-xl ring-1 ring-gray-200 overflow-hidden">
                    <div className="px-3 pt-3 pb-2">
                        <div className="relative">
                            <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                                ref={searchInputRef}
                                type="text"
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    onSearchChange?.(e.target.value);
                                }}
                                placeholder={searchPlaceholder}
                                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-gray-700"
                            />
                        </div>
                    </div>
                    <div className="overflow-y-auto pb-1" style={{ maxHeight: '14rem' }}>
                        {filtered.length === 0 ? (
                            <div className="px-4 py-3 text-sm text-gray-400 text-center">
                                No matches
                            </div>
                        ) : (
                            filtered.map((option) => {
                                const isSelected = option.value === value;
                                return (
                                    <button
                                        key={option.value}
                                        type="button"
                                        onClick={() => handleSelect(option.value)}
                                        className={`flex items-center gap-2 w-full px-4 py-2 text-sm text-left hover:bg-gray-100 transition-colors ${
                                            isSelected ? 'bg-gray-50' : ''
                                        }`}
                                    >
                                        <span className="min-w-0 flex-1">
                                            <span className="block truncate text-gray-700">
                                                {option.label}
                                            </span>
                                            {option.sublabel && (
                                                <span className="block truncate text-[11px] text-gray-400">
                                                    {option.sublabel}
                                                </span>
                                            )}
                                        </span>
                                        {isSelected && (
                                            <CheckIcon className="h-4 w-4 text-gray-700 shrink-0" />
                                        )}
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
